import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Castle, Ship, Camera, MapPin, Waves, Crown, Coffee } from 'lucide-react';
import './GuidePage.css';

export default function EdinburghFifeGuide() {
  return (
    <div className="guide-page">
      <Link to="/wissen" className="back-link"><ArrowLeft size={20} /> Zurück zum Wissen</Link>
      
      <article className="guide-content">
        <h1>🏰 Edinburgh & Fife - Hauptstadt trifft Küste</h1>
        
        <div className="info-box">
          <p>
            Edinburgh = <strong>Schottlands Hauptstadt und UNESCO-Welterbe</strong>. Fife = 
            <strong>"The Kingdom"</strong> mit malerischen Fischerdörfern und St Andrews Golf. 
            Perfekt für Stadt-Kultur + Küsten-Radeln!
          </p>
        </div>

        <section>
          <h2>Übersicht Edinburgh & Fife</h2>
          
          <div className="data-grid">
            <div className="data-item">
              <h3>🏛️ Edinburgh</h3>
              <p>500.000 Einwohner</p>
              <p>UNESCO-Welterbe</p>
              <p>Festival City</p>
            </div>
            <div className="data-item">
              <h3>👑 Fife</h3>
              <p>"Kingdom of Fife"</p>
              <p>112km Küstenlinie</p>
              <p>St Andrews Golf</p>
            </div>
            <div className="data-item">
              <h3>🚴 Charakter</h3>
              <p>Stadt + Küste</p>
              <p>Flach bis hügelig</p>
              <p>Beste Infrastruktur</p>
            </div>
            <div className="data-item">
              <h3>⭐ Schwierigkeit</h3>
              <p>Leicht</p>
              <p>Perfekt für Familien</p>
              <p>Ganzjährig</p>
            </div>
          </div>

          <h3>Warum Edinburgh & Fife?</h3>
          <ul>
            <li><strong>Stadt + Natur:</strong> Kultur in Edinburgh, Küste in Fife</li>
            <li><strong>Beste Infrastruktur:</strong> Radwege, öffentliche Verkehrsmittel, Shops überall</li>
            <li><strong>Familienfreundlich:</strong> Flache Küstenwege, kurze Etappen möglich</li>
            <li><strong>Ganzjährig:</strong> Auch im Winter fahrbar (selten Schnee)</li>
            <li><strong>Kultur-Overload:</strong> Museen, Schlösser, Festivals, Golf, Whisky</li>
            <li><strong>Fotogen:</strong> Pittoreske Fischerdörfer, Edinburgh Castle, Forth Bridge</li>
          </ul>
        </section>

        <section>
          <h2>1. Edinburgh - Die Hauptstadt</h2>
          
          <div className="info-box">
            <p>
              Edinburgh ist eine der schönsten Städte Europas: <strong>Medieval Old Town + 
              Georgian New Town = UNESCO-Welterbe</strong>. Edinburgh Castle thront über allem. 
              Im August = Festival-Hauptstadt der Welt!
            </p>
          </div>

          <h3>Edinburgh mit dem Rad erkunden</h3>
          <div className="warning-box">
            <Ship size={20} />
            <strong>Edinburgh ist hügelig!</strong> Old Town = steil (Kopfsteinpflaster!). 
            Rad für Sightseeing nicht ideal - besser zu Fuß. <strong>Aber: Radwege aus der 
            Stadt raus sind ausgezeichnet!</strong>
          </div>

          <h4>Edinburgh Radrouten</h4>

          <h4>1. Water of Leith Walkway (20km, 200m HM)</h4>
          <ul>
            <li><strong>Route:</strong> Balerno - Leith (Flussweg durch Edinburgh)</li>
            <li><strong>Oberfläche:</strong> Meist asphaltiert, teilweise Gravel</li>
            <li><strong>Highlights:</strong> Dean Village, Scottish Gallery of Modern Art</li>
            <li><strong>Charakter:</strong> Ruhig, grün, mitten durch die Stadt</li>
          </ul>

          <h4>2. Innocent Railway Path (8km, 50m HM)</h4>
          <ul>
            <li><strong>Route:</strong> St Leonards - Brunstane (alter Eisenbahntrasse)</li>
            <li><strong>Oberfläche:</strong> Asphalt, flach</li>
            <li><strong>Perfekt für:</strong> Familie, Pendeln, Zugang zur Küste</li>
          </ul>

          <h4>3. Edinburgh - Cramond Loop (25km, 200m HM)</h4>
          <ul>
            <li><strong>Route:</strong> Zentrum - Cramond Village (Firth of Forth)</li>
            <li><strong>Highlights:</strong> Cramond Island (bei Ebbe erreichbar!)</li>
            <li><strong>Rückweg:</strong> Via Corstorphine Hill oder gleiche Route</li>
          </ul>

          <h3>Must-See Edinburgh (zu Fuß!)</h3>
          <div className="checklist">
            <div className="check-item">
              <Castle /> <strong>Edinburgh Castle:</strong> Auf erloschenen Vulkan (Castle Rock)
            </div>
            <div className="check-item">
              <Castle /> <strong>Royal Mile:</strong> Old Town Hauptstraße (Castle - Holyrood Palace)
            </div>
            <div className="check-item">
              <Castle /> <strong>Arthur's Seat:</strong> 251m Hügel mit Panorama (Wanderung 1h)
            </div>
            <div className="check-item">
              <Castle /> <strong>Holyrood Palace:</strong> Queen's offizieller Wohnsitz
            </div>
            <div className="check-item">
              <Castle /> <strong>Princes Street Gardens:</strong> Park zwischen Old & New Town
            </div>
            <div className="check-item">
              <Castle /> <strong>Scott Monument:</strong> Größtes Schriftsteller-Denkmal der Welt
            </div>
          </div>

          <h3>Edinburgh Festivals</h3>
          <div className="pro-tip">
            <h3>🎭 Festival Season (August)</h3>
            <p>
              Im August explodiert Edinburgh mit Festivals - <strong>aber ACHTUNG: Stadt ist 
              komplett ausgebucht!</strong> Unterkünfte 6+ Monate vorbuchen oder meiden.
            </p>
            <ul>
              <li><strong>Edinburgh Fringe:</strong> Größtes Arts-Festival der Welt (1000+ Shows)</li>
              <li><strong>Edinburgh International Festival:</strong> Klassische Musik, Oper, Theater</li>
              <li><strong>Royal Edinburgh Military Tattoo:</strong> Militärparade auf Castle Esplanade</li>
              <li><strong>Edinburgh Book Festival:</strong> Literatur-Event</li>
            </ul>
          </div>

          <h3>Praktisches Edinburgh</h3>
          <ul>
            <li><strong>Bike Shops:</strong> Viele! Biketrax, Evans Cycles, Leith Cycle Co.</li>
            <li><strong>Bike Hire:</strong> Mehrere Anbieter (ab £20/Tag)</li>
            <li><strong>Öffis:</strong> Tram, Busse (Rad meist nicht erlaubt Rush Hour)</li>
            <li><strong>Sicher:</strong> Gute Infrastruktur, aber aufpassen Tram-Schienen!</li>
          </ul>
        </section>

        <section>
          <h2>2. Fife Coastal Route - Das Highlight!</h2>
          
          <div className="info-box">
            <p>
              Die <strong>Fife Coastal Route (190km)</strong> ist eine der schönsten Küstenrouten 
              UK! Von Kincardine Bridge nach Newburgh, vorbei an malerischen Fischerdörfern, 
              St Andrews und den Forth Bridges.
            </p>
          </div>

          <h3>Route-Übersicht</h3>
          <div className="info-box">
            <ul>
              <li><strong>Start:</strong> Kincardine Bridge (westlich von Edinburgh)</li>
              <li><strong>Ziel:</strong> Newburgh (Tay Estuary)</li>
              <li><strong>Distanz:</strong> 190km</li>
              <li><strong>Höhenmeter:</strong> ~1500m (moderat hügelig)</li>
              <li><strong>Dauer:</strong> 3-5 Tage (entspannt)</li>
              <li><strong>Schwierigkeit:</strong> Leicht-Mittel</li>
              <li><strong>Oberfläche:</strong> 80% Asphalt, 20% Gravel/Trail</li>
              <li><strong>Beste Zeit:</strong> April - Oktober</li>
            </ul>
          </div>

          <h3>Etappenvorschlag Fife Coastal Route (5 Tage)</h3>

          <h4>Tag 1: Edinburgh - North Queensferry (25km, 200m HM)</h4>
          <ul>
            <li><strong>Route:</strong> Via Dalmeny, unter Forth Bridge durch</li>
            <li><strong>Highlights:</strong> Forth Bridge (UNESCO-Welterbe!), Deep Sea World</li>
            <li><strong>Übernachtung:</strong> North Queensferry (kleine Auswahl) oder Inverkeithing</li>
          </ul>

          <h4>Tag 2: North Queensferry - Anstruther (60km, 500m HM)</h4>
          <ul>
            <li><strong>Route:</strong> Via Burntisland, Kirkcaldy, East Neuk</li>
            <li><strong>Highlights:</strong> Burntisland Beach, Ravenscraig Castle, East Neuk Villages</li>
            <li><strong>Übernachtung:</strong> Anstruther (Fischerdorf, viele B&Bs)</li>
            <li><strong>Tipp:</strong> Anstruther Fish Bar (beste Fish & Chips Schottlands!)</li>
          </ul>

          <h4>Tag 3: Anstruther - St Andrews (20km, 200m HM)</h4>
          <ul>
            <li><strong>Route:</strong> Via Crail (schönstes Dorf!)</li>
            <li><strong>Highlights:</strong> Crail Harbour, St Andrews (Golf-Hauptstadt)</li>
            <li><strong>Übernachtung:</strong> St Andrews (Stadt erkunden!)</li>
            <li><strong>Kurze Etappe:</strong> Zeit für Besichtigungen</li>
          </ul>

          <h4>Tag 4: St Andrews - St Monans (35km, 300m HM)</h4>
          <ul>
            <li><strong>Route:</strong> Zurück Richtung Süden (East Neuk Loop)</li>
            <li><strong>Highlights:</strong> Pittenweem, St Monans Windmill</li>
            <li><strong>Alternative:</strong> Nach Norden Richtung Tay (siehe Tag 5 Alternative)</li>
          </ul>

          <h4>Tag 5: St Monans - Edinburgh Return (70km, 600m HM)</h4>
          <ul>
            <li><strong>Route:</strong> Via Kirkcaldy, Forth Bridge zurück</li>
            <li><strong>Alternative:</strong> Weiter nach Newburgh (Nordroute, +50km)</li>
          </ul>

          <h3>Die East Neuk Villages - Das Highlight!</h3>
          <div className="pro-tip">
            <h3><Camera size={20} /> East Neuk of Fife</h3>
            <p>
              "East Neuk" = Ostecke Fifes mit <strong>den pittoreskesten Fischerdörfern 
              Schottlands!</strong> Farbenfroh, Hafen, Kopfsteinpflaster - Instagram-Traum!
            </p>
          </div>

          <div className="checklist">
            <div className="check-item">
              <Ship /> <strong>Crail:</strong> Schönstes Dorf! Roter Sandstein, Hafen, Lobster-Shack
            </div>
            <div className="check-item">
              <Ship /> <strong>Anstruther:</strong> Größtes Dorf, Fish Bar, Scottish Fisheries Museum
            </div>
            <div className="check-item">
              <Ship /> <strong>Pittenweem:</strong> Aktiver Fischerhafen, Künstler-Gemeinschaft
            </div>
            <div className="check-item">
              <Ship /> <strong>St Monans:</strong> Windmühle am Meer, ruhiger als andere
            </div>
            <div className="check-item">
              <Ship /> <strong>Elie:</strong> Langer Sandstrand, Ship Inn (Beach-Pub!)
            </div>
          </div>

          <h3>St Andrews - Golf-Mekka</h3>
          <div className="info-box">
            <h4><Crown size={20} /> The Home of Golf</h4>
            <ul>
              <li><strong>Old Course:</strong> Ältester Golfplatz der Welt (1400s)</li>
              <li><strong>Swilcan Bridge:</strong> Berühmteste Golfbrücke (18. Loch)</li>
              <li><strong>British Golf Museum:</strong> Geschichte des Sports</li>
              <li><strong>St Andrews Cathedral:</strong> Ruine, größte Kirche Schottlands (war)</li>
              <li><strong>St Andrews Castle:</strong> Mit Bottle Dungeon (gruselig!)</li>
              <li><strong>West Sands Beach:</strong> Chariots of Fire Drehort</li>
              <li><strong>University:</strong> Drittälteste UK (1413), William & Kate studierten hier</li>
            </ul>
          </div>

          <h3>Forth Bridges - UNESCO-Welterbe</h3>
          <ul>
            <li><strong>Forth Rail Bridge (1890):</strong> Ikonische rote Brücke, UNESCO seit 2015</li>
            <li><strong>Forth Road Bridge (1964):</strong> Hängebrücke (jetzt Busse/Räder)</li>
            <li><strong>Queensferry Crossing (2017):</strong> Neue Brücke (Autos)</li>
            <li><strong>Mit Rad:</strong> Über Forth Road Bridge nach Edinburgh (spektakulär!)</li>
          </ul>
        </section>

        <section>
          <h2>3. Inland Fife - Dunfermline & Around</h2>
          
          <h3>Dunfermline - Alte Hauptstadt</h3>
          <div className="info-box">
            <p>
              Dunfermline war Schottlands Hauptstadt bis 1603! <strong>Robert the Bruce 
              ist hier begraben</strong> (Dunfermline Abbey).
            </p>
          </div>

          <ul>
            <li><strong>Dunfermline Abbey:</strong> Robert the Bruce Grab</li>
            <li><strong>Andrew Carnegie Birthplace:</strong> Industrieller & Philanthrop</li>
            <li><strong>Pittencrieff Park:</strong> "The Glen" - schöner Stadtpark</li>
          </ul>

          <h4>Dunfermline - Loch Leven Loop (60km, 400m HM)</h4>
          <ul>
            <li><strong>Route:</strong> Via Loch Leven Heritage Trail</li>
            <li><strong>Highlights:</strong> Loch Leven Castle (Mary Queen of Scots gefangen!)</li>
            <li><strong>Wildlife:</strong> Vogelbeobachtung (RSPB Reserve)</li>
          </ul>

          <h3>Falkland Palace</h3>
          <ul>
            <li><strong>Renaissance-Palast:</strong> Ehemaliges Royal Jagdschloss</li>
            <li><strong>Von St Andrews:</strong> 30km inland (schöne Route durch Howe of Fife)</li>
            <li><strong>Outlander-Fans:</strong> Diente als Kulisse für Inverness</li>
          </ul>
        </section>

        <section>
          <h2>4. Lothians - Edinburgh's Umgebung</h2>
          
          <h3>East Lothian - Die Riviera Schottlands</h3>
          <div className="info-box">
            <p>
              East Lothian = Küste östlich von Edinburgh. <strong>Weniger Regen, 
              wärmer, viele Sandstrände</strong> - deshalb "Scottish Riviera"!
            </p>
          </div>

          <h4>Edinburgh - North Berwick Coastal Route (40km, 300m HM)</h4>
          <ul>
            <li><strong>Route:</strong> Via Musselburgh, Gullane, Dirleton</li>
            <li><strong>Highlights:</strong> Gullane Beach, Dirleton Castle, North Berwick (Seevögel!)</li>
            <li><strong>Bass Rock:</strong> Weltgrößte Gannet-Kolonie (Bootstouren ab North Berwick)</li>
            <li><strong>Scottish Seabird Centre:</strong> North Berwick (Live-Kameras auf Bass Rock)</li>
          </ul>

          <h4>Dunbar - Tantallon Castle (20km, 200m HM)</h4>
          <ul>
            <li><strong>Tantallon Castle:</strong> Dramatische Klippen-Burg (gegenüber Bass Rock)</li>
            <li><strong>Dunbar:</strong> John Muir (Naturschutz-Pionier) Geburtsort</li>
          </ul>

          <h3>West Lothian - Inland</h3>
          <ul>
            <li><strong>Linlithgow Palace:</strong> Geburtsort Mary Queen of Scots (30km westlich Edinburgh)</li>
            <li><strong>Union Canal:</strong> Edinburgh - Falkirk (flacher Radweg, 50km)</li>
            <li><strong>Falkirk Wheel:</strong> Rotierendes Schiffshebewerk (einzigartig!)</li>
          </ul>

          <h3>Midlothian - Südlich Edinburgh</h3>
          <ul>
            <li><strong>Rosslyn Chapel:</strong> Berühmte Kapelle (Da Vinci Code), 12km südlich</li>
            <li><strong>Pentland Hills:</strong> Regional Park, Off-Road Trails</li>
          </ul>
        </section>

        <section>
          <h2>5. Praktische Infos Edinburgh & Fife</h2>
          
          <h3>Infrastruktur & Radfreundlichkeit</h3>
          <div className="pro-tip">
            <h3>✅ Beste Rad-Infrastruktur Schottlands!</h3>
            <ul>
              <li><strong>Radwege:</strong> Ausgezeichnetes Netz (Edinburgh + Fife)</li>
              <li><strong>National Cycle Network:</strong> Route 1 (Fife Coast), Route 76 (Edinburgh)</li>
              <li><strong>Beschilderung:</strong> Gut (blaue Schilder)</li>
              <li><strong>Bike Shops:</strong> Überall (Edinburgh = mindestens 20!)</li>
              <li><strong>Cafés:</strong> Viele Bike-friendly Cafés</li>
              <li><strong>Unterkünfte:</strong> Alle Kategorien, meist mit Radabstellraum</li>
            </ul>
          </div>

          <h3>Öffentliche Verkehrsmittel + Rad</h3>
          <ul>
            <li><strong>Zug Edinburgh - Fife:</strong> Rad erlaubt (außer Rush Hour)</li>
            <li><strong>Lothian Buses:</strong> Rad nicht erlaubt</li>
            <li><strong>ScotRail:</strong> Rad-Reservierung empfohlen (gratis)</li>
            <li><strong>Tram Edinburgh:</strong> Rad erlaubt (außer Peak Times)</li>
          </ul>

          <h3>Wetter & Beste Reisezeit</h3>
          <div className="data-grid">
            <div className="data-item">
              <strong>April - Juni:</strong> Beste Zeit! Trocken, nicht zu voll
            </div>
            <div className="data-item">
              <strong>Juli - August:</strong> Warm, aber Edinburgh Festival = voll + teuer
            </div>
            <div className="data-item">
              <strong>September - Oktober:</strong> Ruhiger, gutes Wetter, Herbstfarben
            </div>
            <div className="data-item">
              <strong>November - März:</strong> Kühl, möglich (selten Schnee an Küste)
            </div>
          </div>

          <h3>Wetter-Facts</h3>
          <ul>
            <li><strong>Edinburgh:</strong> Trockenste Stadt UK Ostküste! (~650mm/Jahr)</li>
            <li><strong>Fife:</strong> Ähnlich trocken, "Sunshine Coast"</li>
            <li><strong>Wind:</strong> Küste oft windig (wie überall in Schottland)</li>
            <li><strong>Schnee:</strong> Selten, meist nur in Pentland Hills</li>
          </ul>
        </section>

        <section>
          <h2>6. Essen & Trinken</h2>
          
          <h3>Edinburgh Food Scene</h3>
          <div className="pro-tip">
            <h3><Coffee size={20} /> Edinburgh = Foodie-Stadt!</h3>
            <p>
              Edinburgh hat Michelin-Sterne, Street Food, Craft Beer und Whisky-Bars en masse!
            </p>
            <ul>
              <li><strong>Haggis probieren:</strong> Traditional (Balmoral, Whiski Bar) oder Modern (Oink)</li>
              <li><strong>Fish & Chips:</strong> L'Alba D'Oro (beste der Stadt)</li>
              <li><strong>Craft Beer:</strong> Brewdog, Innis & Gunn Taproom</li>
              <li><strong>Whisky:</strong> The Scotch Whisky Experience (Castle Hill)</li>
              <li><strong>Café-Kultur:</strong> Artisan Roast, Lovecrumbs, Fortitude Coffee</li>
            </ul>
          </div>

          <h3>Fife Seafood</h3>
          <ul>
            <li><strong>Anstruther Fish Bar:</strong> Legendär! Best Fish & Chips UK (oft Schlange)</li>
            <li><strong>Crail Lobster:</strong> Frischer Lobster direkt am Hafen</li>
            <li><strong>The Cellar (Anstruther):</strong> Michelin-empfohlen, Seafood-Restaurant</li>
            <li><strong>The Ship Inn (Elie):</strong> Pub direkt am Strand</li>
          </ul>
        </section>

        <section>
          <h2>7. Familienfreundlich & Anfänger</h2>
          
          <h3>Warum perfekt für Familien?</h3>
          <ul>
            <li><strong>Flache Routen:</strong> Küste meist eben</li>
            <li><strong>Kurze Etappen:</strong> Dörfer alle 10-15km</li>
            <li><strong>Viel zu sehen:</strong> Schlösser, Burgen, Strände (Kinder-Entertainment!)</li>
            <li><strong>Sicher:</strong> Gute Radwege, wenig Verkehr auf Nebenstraßen</li>
            <li><strong>Infrastruktur:</strong> Cafés, Toiletten, Spielplätze überall</li>
            <li><strong>Bike Hire auch für Kinder:</strong> Kinderräder, Anhänger verfügbar</li>
          </ul>

          <h3>Top Familie-Routen</h3>
          <ol>
            <li><strong>Cramond - South Queensferry (20km return):</strong> Flach, Forth Bridge Aussicht</li>
            <li><strong>North Queensferry - Burntisland (25km):</strong> Fife Coastal, Strand, einfach</li>
            <li><strong>Anstruther - Crail (10km return):</strong> Kurz, schön, Fish & Chips!</li>
            <li><strong>Water of Leith (Edinburgh, 10km):</strong> Flach, grün, Stadtpark</li>
          </ol>
        </section>

        <div className="pro-tip">
          <h3>🎯 Top 5 Edinburgh & Fife Must-Dos</h3>
          <ol>
            <li><strong>Fife Coastal Route:</strong> Die komplette 190km Route (3-5 Tage)</li>
            <li><strong>East Neuk Villages:</strong> Crail, Anstruther, Pittenweem (Fish & Chips!)</li>
            <li><strong>Edinburgh Sightseeing:</strong> Castle, Royal Mile, Arthur's Seat (zu Fuß!)</li>
            <li><strong>Forth Bridge Überquerung:</strong> Per Rad über die UNESCO-Brücke</li>
            <li><strong>St Andrews:</strong> Golf-Pilgerort, Cathedral, West Sands Beach</li>
          </ol>
        </div>

        <div className="info-box">
          <h4>📚 Weiterführende Guides</h4>
          <ul>
            <li><Link to="/wissen/ausruestung">🎒 Ausrüstung</Link> - Weniger Extrem-Ausrüstung nötig</li>
            <li><Link to="/wissen/fahrtechnik">🚴 Fahrtechnik</Link> - Entspanntes Küstenfahren</li>
            <li><Link to="/wissen/sicherheit">🦺 Sicherheit</Link> - Stadt-Sicherheit</li>
          </ul>
        </div>

        <div className="download-section">
          <h3>📥 Downloads</h3>
          <button className="download-btn" onClick={() => window.print()}>
            Edinburgh & Fife Guide als PDF speichern
          </button>
        </div>
      </article>
    </div>
  );
}
